import {
  CommandBar,
  DetailsListLayoutMode,
  IColumn,
  Icon,
  Link,
  SelectionMode,
  ShimmeredDetailsList,
  Spinner,
  SpinnerSize,
  Stack,
  CommandBarButton,
  MessageBar,
  MessageBarType,
  TooltipHost,
  TooltipDelay,
  ITooltipHostStyles,
} from "@fluentui/react";
import React from "react";
import validateFile from "./validateFiles";
import { RouteComponentProps, withRouter } from "react-router-dom";
import AccessAPI from "../../store/AccessAPI";
import { Utility } from "../../shared/models/Helper";
import { BulkRemovalPanelProps } from "../../shared/models/BulkRemovalPanelProps.model";
import BulkRemovalPanel from "./BulkRemovalPanel";
import { BulkRemovalRequest } from "../../shared/models/BulkRemovalRequest.model";
import formatDate from "../../shared/functions/DateFormatter";

interface IBulkRemovalProps extends RouteComponentProps<any> {}

interface IBulkRemovalState {
  isPanelOpen: boolean;
  file: File | null;
  reasonForRemoval: string;
  reasonForRemovalCustomText: string;
  dataLoaded: boolean;
  inputValue: string;
  fileValidationError: string[] | null;
  reasonForRemovalError: string | null;
  saveInProgress: boolean;
  fileErrorMessage: string;
  requests: BulkRemovalRequest[];
  isLoading: boolean;
  loadingErrorMessage?: string;
  downloadInProgressRequestId: string | null;
  currentPage: number;
  pageSize: number;
}

class BulkRemoval extends React.Component<
  IBulkRemovalProps,
  IBulkRemovalState
> {
  tenantData: any;
  constructor(props: IBulkRemovalProps) {
    super(props);
    const tenantName = props.match.params.tenantName;
    this.tenantData = Utility.GetTenantData().filter(
      (x) => x.TenantName.toLowerCase() === tenantName.toLowerCase()
    )[0];

    this.state = {
      isPanelOpen: false,
      file: null,
      reasonForRemoval: "",
      reasonForRemovalCustomText: "",
      dataLoaded: false,
      inputValue: "",
      fileValidationError: null,
      reasonForRemovalError: null,
      saveInProgress: false,
      fileErrorMessage: "",
      requests: [],
      isLoading: false,
      downloadInProgressRequestId: null,
      loadingErrorMessage: "",
      currentPage: 1,
      pageSize: 10,
    };
  }

  componentDidMount() {
    this.getBulkRemovalRequests();
  }

  getBulkRemovalRequests = () => {
    this.setState({ isLoading: true, loadingErrorMessage: "" });

    AccessAPI.getBulkRemovalRequestsAsync(this.tenantData.TenantId)
      .then(async (requests) => {
        if (requests && Array.isArray(requests)) {
          const formattedRequests = requests.map((request) => ({
            ...request,
            key: request.id,
            RequestedDate: formatDate(request.RequestedDate),
            CompletedDate: request.ProcessingEndDate
              ? formatDate(request.ProcessingEndDate)
              : "",
          }));
          this.setState({ requests: formattedRequests, isLoading: false });
        } else {
          throw new Error(
            "Unexpected response format for bulk removal requests."
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching bulk removal requests:", error);
        this.setState({
          loadingErrorMessage:
            "An error occurred while fetching bulk removal requests.",
          isLoading: false,
        });
      });
  };

  exportBulkRemovalRequest = (requestId: string) => {
    this.setState({
      downloadInProgressRequestId: requestId,
    });
    AccessAPI.exportBulkRemovalRequestAsync(requestId)
      .then(async (res) => {
        if (res && "data" in res && res.data instanceof Blob) {
          const fileName = res.headers["content-disposition"]
            ? res.headers["content-disposition"]
                .split("filename=")[1]
                .split(";")[0]
                .replace(/"/g, "")
            : "BulkRemovalRequest.xlsx";

          const anchor = document.createElement("a");
          anchor.href = window.URL.createObjectURL(res.data);
          anchor.download = fileName;
          anchor.click();
          window.URL.revokeObjectURL(anchor.href);
        } else if (res && "message" in res) {
          const errorMessage =
            res.message ?? "Unknown error downloading template";
          throw new Error(errorMessage);
        } else {
          throw new Error(
            "Unexpected response format while downloading template."
          );
        }
      })
      .catch((error) => {
        console.error("Error exporting bulk removal requests:", error);
      })
      .finally(() => {
        this.setState({
          downloadInProgressRequestId: null,
        });
      });
  };

  downloadTemplateFile = () => {
    AccessAPI.getBulkRemovalTemplate(this.tenantData?.TenantName).then(async (res) => {
      if (res && "data" in res && res.data instanceof Blob) {
        const anchor = document.createElement("a");
        anchor.href = window.URL.createObjectURL(res.data);
        anchor.download = "BulkRemovalTemplate.xlsx";
        anchor.click();
        window.URL.revokeObjectURL(anchor.href);
      } else if (res && "message" in res) {
        const errorMessage =
          res.message ?? "Unknown error downloading template";
        throw new Error(errorMessage);
      } else {
        throw new Error(
          "Unexpected response format while downloading template."
        );
      }
    });
  };

  submitRequest = (formData: FormData) => {
    this.setState({ saveInProgress: true });
    AccessAPI.submitBulkRemovalRequestAsync(formData).then((apiResponse) => {
      this.setState({ saveInProgress: false });

      if (apiResponse.success) {
        // Handle successful submission
        this.togglePanel();
        this.setState({
          file: null,
          inputValue: "",
          fileErrorMessage: "",
          dataLoaded: false,
          reasonForRemoval: "",
          reasonForRemovalCustomText: "",
          fileValidationError: null,
        });
        this.getBulkRemovalRequests();
      } else {
        // Handle validation errors
        if (apiResponse.errors && apiResponse.errors.length > 0) {
          const errorMessages = apiResponse.errors.map((e) => e.errorMessage);
          this.setState({
            fileValidationError: errorMessages,
            fileErrorMessage: "",
          });
        } else {
          // Handle an unexpected error from the API
          this.setState({
            fileValidationError: null,
            fileErrorMessage: "An error occurred while processing the request.",
          });
        }
      }
    });
  };

  togglePanel = (): void => {
    this.setState((prevState) => ({ isPanelOpen: !prevState.isPanelOpen }));
    this.setState({
      reasonForRemoval: "",
      reasonForRemovalCustomText: "",
      fileValidationError: null,
      fileErrorMessage: "",
      dataLoaded: false,
      inputValue: "",
      file: null,
      reasonForRemovalError: null,
    });
  };

  addTimestampBeforeExtension = (fileName: string): string =>
    fileName.replace(/(\.[\w\d_-]+)$/i, `${new Date().getTime()}$1`);

  onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({ fileValidationError: null });

    if (
      !event.target.files ||
      !event.target.files.length ||
      event.target.files.length === 0
    ) {
      return;
    }
    let filesToUpload = Array<any>();
    let payloadSize = 0;
    const file = event.target.files.item(0);
    const fileNameWithTimestamp = this.addTimestampBeforeExtension(file.name);
    const newFileObject = {
      file,
      fileNameWithTimeStamp: fileNameWithTimestamp,
      fileName: file.name,
      fileType: file.type,
      validationErrors: validateFile(file),
    };
    filesToUpload = filesToUpload.concat([newFileObject]);
    payloadSize += file.size;

    const filesWithErrors = filesToUpload.filter(
      (fileToUpload) =>
        fileToUpload.validationErrors &&
        fileToUpload.validationErrors.length > 0
    );

    if (filesWithErrors.length > 0) {
      this.setState({
        fileValidationError: [filesWithErrors[0].validationErrors[0].title],
      });
    } else {
      this.setState({
        inputValue: file.name,
        file: event.target.files[0],
        dataLoaded: true,
      });
    }
  };

  handleUploadFileButtonClick = () => {
    this.setState({ fileErrorMessage: "", reasonForRemovalError: null });

    const { file, reasonForRemoval, reasonForRemovalCustomText } = this.state;

    if (!file) {
      this.setState({ fileErrorMessage: "Please select a file." });
      return;
    }

    if (!reasonForRemoval) {
      this.setState({
        reasonForRemovalError: "Please provide a reason for removal.",
      });
      return;
    }

    if (reasonForRemoval === "OTHER" && !reasonForRemovalCustomText) {
      this.setState({
        reasonForRemovalError: "Please provide details for 'Other' reason.",
      });
      return;
    }

    this.setState({ saveInProgress: true });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("tenantId", this.tenantData.TenantId);
    formData.append(
      "businessJustification",
      reasonForRemoval === "Other"
        ? reasonForRemovalCustomText
        : reasonForRemoval
    );
    this.submitRequest(formData);
  };

  tooltipHostStyles: ITooltipHostStyles = {
    root: {
      textOverflow: "ellipsis",
      overflow: "hidden",
    },
  };

  columns: IColumn[] = [
    {
      key: "requestedDate",
      name: "Date Requested",
      fieldName: "RequestedDate",
      minWidth: 125,
      maxWidth: 125,
    },
    {
      key: "completedDate",
      name: "Date Completed",
      fieldName: "CompletedDate",
      minWidth: 125,
      maxWidth: 125,
    },
    {
      key: "requestedByUPN",
      name: "Requested By",
      fieldName: "RequestedByUPN",
      minWidth: 250,
      maxWidth: 250,
      onRender: (item: BulkRemovalRequest) => {
        const requestedBy = item.RequestedByUPN;
        return (
          <TooltipHost
            delay={TooltipDelay.medium}
            id={item.id}
            key={item.id}
            closeDelay={500}
            styles={this.tooltipHostStyles}
            tooltipProps={{
              onRenderContent: () => <>{requestedBy}</>,
            }}
          >
            <>{requestedBy}</>
          </TooltipHost>
        );
      },
    },
    {
      key: "businessJustification",
      name: "Business Justification",
      fieldName: "BusinessJustification",
      minWidth: 300,
      maxWidth: 300,
      onRender: (item: BulkRemovalRequest) => {
        const businessJustification = item.BusinessJustification;
        return (
          <TooltipHost
            delay={TooltipDelay.medium}
            id={item.id}
            key={item.id}
            closeDelay={500}
            styles={this.tooltipHostStyles}
            tooltipProps={{
              onRenderContent: () => <>{businessJustification}</>,
            }}
          >
            <>{businessJustification}</>
          </TooltipHost>
        );
      },
    },
    {
      key: "status",
      name: "Request Status",
      fieldName: "Result",
      minWidth: 150,
      maxWidth: 150,
    },
    {
      key: "results",
      name: "Results",
      fieldName: "Results",
      minWidth: 150,
      maxWidth: 150,
      onRender: (item: BulkRemovalRequest) => this.downloadResultsLink(item),
    },
  ];

  downloadResultsLink = (bulkRemovalRequest: BulkRemovalRequest) => {
    const isDownloading =
      this.state.downloadInProgressRequestId === bulkRemovalRequest.id;

    return isDownloading ? (
      <React.Fragment>
        <Spinner
          size={SpinnerSize.small}
          styles={{ root: { marginLeft: 5 } }}
        />
      </React.Fragment>
    ) : (
      <Link
        href=""
        target="_blank"
        style={{ textDecoration: "none" }}
        disabled={bulkRemovalRequest.Result !== "Completed"}
        onClick={(e) => {
          e.preventDefault();
          this.exportBulkRemovalRequest(bulkRemovalRequest.id);
        }}
      >
        Download
        <Icon iconName="Download" style={{ marginLeft: 5 }} />
      </Link>
    );
  };

  newRequestCommandBarButton = () => {
    return (
      <CommandBarButton
        iconProps={{ iconName: "Add" }}
        text="New Bulk Removal Request"
        onClick={this.togglePanel}
      />
    );
  };

  getBulkRemovalPanelProps = (): BulkRemovalPanelProps => {
    return {
      closePanel: this.togglePanel,
      tenantId: this.tenantData?.TenantId,
      tenantName: this.tenantData?.TenantName,
      onSubmit: this.handleUploadFileButtonClick,
      onFileInputChange: this.onFileInputChange,
      setReasonForRemovalChange: (val: string) =>
        this.setState({ reasonForRemoval: val }),
      setReasonForRemovalCustomTextChange: (val: string) =>
        this.setState({ reasonForRemovalCustomText: val }),
      reasonForRemoval: this.state.reasonForRemoval,
      reasonForRemovalCustomText: this.state.reasonForRemovalCustomText,
      setFileValidationErrors: (val: string[] | null) =>
        this.setState({ fileValidationError: val }),
      fileValidationErrors: this.state.fileValidationError,
      setFormValidationError: (val: string) =>
        this.setState({ fileErrorMessage: val }),
      formValidationError: this.state.fileErrorMessage,
      dataLoaded: this.state.dataLoaded,
      setDataLoaded: (val: boolean) => this.setState({ dataLoaded: val }),
      saveInProgress: this.state.saveInProgress,
      setSaveInProgress: (val: boolean) =>
        this.setState({ saveInProgress: val }),
      inputValue: this.state.inputValue,
      setInputValue: (val: string) => this.setState({ inputValue: val }),
      reasonForRemovalError: this.state.reasonForRemovalError,
      setReasonForRemovalError: (val: string | null) =>
        this.setState({ reasonForRemovalError: val }),
      file: this.state.file,
      setFile: (val: File | null) => this.setState({ file: val }),
      fileErrorMessage: this.state.fileErrorMessage,
      setFileErrorMessage: (val: string) =>
        this.setState({ fileErrorMessage: val }),
      downloadFile: this.downloadTemplateFile,
    };
  };

  getPagedRequests = () => {
    const { requests, currentPage, pageSize } = this.state;
    const startIndex = (currentPage - 1) * pageSize;
    return requests.slice(startIndex, startIndex + pageSize);
  };

  renderPagination = () => {
    const { requests, currentPage, pageSize } = this.state;
    const totalPages = Math.ceil(requests.length / pageSize);
    if (totalPages <= 1) return null;

    return (
      <Stack horizontal tokens={{ childrenGap: 8 }} style={{ marginTop: 10 }}>
        <CommandBarButton
          iconProps={{ iconName: "ChevronLeft" }}
          text="Previous Page"
          disabled={currentPage === 1}
          onClick={() => this.setState({ currentPage: currentPage - 1 })}
        />
        <span>Page {currentPage} of {totalPages}</span>
        <CommandBarButton
          iconProps={{ iconName: "ChevronRight" }}
          text="Next Page"
          disabled={currentPage === totalPages}
          onClick={() => this.setState({ currentPage: currentPage + 1 })}
        />
      </Stack>
    );
  };

  render() {
    return (
      <Stack
        tokens={{ childrenGap: 10 }}
        style={{ marginLeft: 20, padding: "1%", paddingTop: "2%" }}
      >
        <div id={"div-msg-area"} style={{ height: 25 }}></div>
        <h1 style={{ fontSize: 20 }}>Bulk Removal Requests</h1>
        <CommandBar
          items={[
            {
              key: "newRequest",
              commandBarButtonAs: () => this.newRequestCommandBarButton(),
            },
          ]}
          ariaLabel="Task Actions"
          primaryGroupAriaLabel="Task actions"
          farItemsGroupAriaLabel="Task actions"
          styles={{
            root: { paddingLeft: 0 },
          }}
        />
        {this.state.downloadInProgressRequestId !== null && (
          <MessageBar
            messageBarType={MessageBarType.warning}
            styles={{ root: { padding: 5, marginBottom: 20 } }}
          >
            Downloading results. Please wait...
          </MessageBar>
        )}
        {this.state.loadingErrorMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            styles={{ root: { padding: 5, marginBottom: 20 } }}
          >
            {this.state.loadingErrorMessage}
          </MessageBar>
        )}
        <ShimmeredDetailsList
          setKey="items"
          columns={this.columns}
          items={this.getPagedRequests()}
          enableShimmer={this.state.isLoading}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
          ariaLabelForShimmer="Content is being fetched"
          ariaLabelForGrid="User Management List"
          listProps={{ renderedWindowsAhead: 0, renderedWindowsBehind: 0 }}
          viewport={{ width: 500, height: 500 }}
          detailsListStyles={{
            root: { overflow: "visible", width: 500 },
            contentWrapper: { width: 500 },
          }}
        />
        {this.renderPagination()}
        {this.state.isPanelOpen && (
          <BulkRemovalPanel
            bulkRemovalPanelProps={this.getBulkRemovalPanelProps()}
          />
        )}
      </Stack>
    );
  }
}

export default withRouter(BulkRemoval);
